import { useState, useEffect, useMemo } from "react";
import { useModal } from "../../../../hooks/useModal";
import checkPermission from "../../../../utils/permission";
import { Video, Volume2, Image as ImageIcon, FileText, File as FileIcon, Code2, ClipboardCheck, FileSpreadsheet } from "lucide-react";

import { ApiService } from "../../../../services/api";
import Modal from "../../../../components/ui/Modal";
import type { ChoiceKey, QuestionBankDraft, QuestionBankQuestion } from "../../../../types/question-bank";
import { extractPlainTextFromRichContent } from "../../../../utils/questionRichContent";
export default function ContentModal({
  lessonId,
  sectionId,
  isOpen,
  onClose,
  contentId = null,
}: {
  lessonId: string;
  sectionId: string;
  isOpen: boolean;
  onClose: () => void;
  contentId?: string | null;
}) {
  const { showAlert } = useModal();
  const canCreate = checkPermission("lesson_section_add");
  const canUpdate = checkPermission("lesson_section_update");
  const canViewQuestionBanks = checkPermission("question_bank_list");
  const [content, setContent] = useState({
    title: "",
    content: "",
    type: "VIDEO",
    metadata: {},
  });

  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [existingVideoSourceUrl, setExistingVideoSourceUrl] = useState("");
  const [existingVideoName, setExistingVideoName] = useState("");
  const [videoSourceMode, setVideoSourceMode] = useState<"vimeo" | "file">("vimeo");
  const [vimeoUrl, setVimeoUrl] = useState("");
  const [showQuestionBankImport, setShowQuestionBankImport] = useState(false);
  const [questionBanks, setQuestionBanks] = useState<QuestionBankDraft[]>([]);
  const [questionBanksLoading, setQuestionBanksLoading] = useState(false);
  const [selectedQuestionBankId, setSelectedQuestionBankId] = useState("");

  const extractVimeoVideoId = (url: string): string | null => {
    if (!url) return null;
    const vimeoRegex = /(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/;
    const match = url.match(vimeoRegex);
    return match ? match[1] : null;
  };

  const validateVimeoUrl = (url: string): boolean => extractVimeoVideoId(url) !== null;

  const normalizeVimeoUrl = (raw: string): string => {
    if (!raw) return "";
    const hasAtPrefix = /^@+/.test(raw.trim());
    const cleaned = raw.trim().replace(/^@+/, "");
    const id = extractVimeoVideoId(cleaned);
    const normalized = id ? `https://vimeo.com/${id}` : cleaned;
    return hasAtPrefix ? `@${normalized}` : normalized;
  };

  const isLegacyVimeoUrl = (value: string): boolean => {
    if (!value) return false;
    const cleaned = value.trim().replace(/^@+/, "");
    return /(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/.test(cleaned);
  };

  type ExamQuestionType = "TEXT" | "IMAGE" | "VIDEO" | "AUDIO" | "FILE" | "CODE";

  interface ExamQuestion {
    id: string;
    type: ExamQuestionType;
    question: string;
    options: string[];
    correctAnswer: string | string[];
    metadata?: any;
  }

  const TYPES = [
    {
      key: "VIDEO",
      label: "Video",
      icon: Video,
      requiresFile: videoSourceMode === "file",
      requiresVimeoUrl: videoSourceMode === "vimeo",
      acceptedFiles: "video/*,.mp4,.mov,.webm,.mkv,.avi",
    },
    {
      key: "AUDIO",
      label: "Ses",
      icon: Volume2,
      requiresFile: true,
      acceptedFiles: "audio/*,.m4a,.mp3,.wav,.ogg",
    },
    {
      key: "IMAGE",
      label: "Resim",
      icon: ImageIcon,
      requiresFile: true,
      acceptedFiles: "image/jpeg,image/jpg,.jpg,.jpeg",
    },
    { key: "TEXT", label: "Metin", icon: FileText, requiresFile: false },
    {
      key: "FILE",
      label: "Dosya",
      icon: FileIcon,
      requiresFile: true,
      acceptedFiles: "*/*",
    },
    { key: "CODE", label: "Kod", icon: Code2, requiresFile: false },
    { key: "EXAM", label: "Test", icon: ClipboardCheck, requiresFile: false },
    { key: "QUESTION_BANK_IMPORT", label: "Dizgiciden Test Aktar", icon: FileSpreadsheet, requiresFile: false },
  ];

  const selectedQuestionBank = useMemo(
    () => questionBanks.find((item) => item.id === selectedQuestionBankId || item.originalQuestionBankId === selectedQuestionBankId),
    [questionBanks, selectedQuestionBankId]
  );
  const isQuestionBankImport = content.type === "QUESTION_BANK_IMPORT";
  const effectiveContentType = isQuestionBankImport ? "EXAM" : content.type;

  useEffect(() => {
    if (contentId && isOpen) {
      const fetchContent = async () => {
        try {
          const res = await ApiService.getContent(lessonId, sectionId, contentId);
          const currentVideoSourceUrl = res.type === "VIDEO" ? res.media?.url || res.content || "" : "";
          const isLegacyVideo = res.type === "VIDEO" && !res.media?.url && isLegacyVimeoUrl(res.content || "");
          setContent({
            title: res.title || "",
            content: isLegacyVideo ? res.metadata?.videoDescription || "" : res.content || "",
            type: res.type || "VIDEO",
            metadata: res.metadata || {},
          });
          if (res.type === "VIDEO") {
            if (isLegacyVideo) {
              setVideoSourceMode("vimeo");
              setVimeoUrl(normalizeVimeoUrl(res.content || ""));
            } else {
              setVideoSourceMode("file");
              setVimeoUrl("");
            }
          }
          setExistingVideoSourceUrl(currentVideoSourceUrl);
          setExistingVideoName(res.media?.name || "");
          setUploadedFile(null);
        } catch (error) {
          console.error("İçerik yüklenemedi:", error);
        }
      };
      fetchContent();
    } else if (isOpen) {
      setContent({
        title: "",
        content: "",
        type: "VIDEO",
        metadata: {},
      });
      setUploadedFile(null);
      setUploadProgress(0);
      setExistingVideoSourceUrl("");
      setExistingVideoName("");
      setVideoSourceMode("vimeo");
      setVimeoUrl("");
      setShowQuestionBankImport(false);
      setSelectedQuestionBankId("");
    }
  }, [isOpen, contentId]);

  const loadQuestionBanks = async () => {
    if (!canViewQuestionBanks) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Dizgici kayıtlarını görüntüleme yetkiniz yok.",
        type: "error",
      });
      return;
    }

    setShowQuestionBankImport(true);
    if (questionBanks.length > 0) return;

    setQuestionBanksLoading(true);
    try {
      const response = await ApiService.getQuestionBanks({
        page: 1,
        limit: 100,
        sortBy: "updatedAt",
        sortOrder: "DESC",
      });
      setQuestionBanks(response?.data || []);
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Dizgici kayıtları yüklenemedi.",
        type: "error",
      });
    } finally {
      setQuestionBanksLoading(false);
    }
  };

  const getQuestionBankImportId = (draft: QuestionBankDraft): string => draft.id;

  const normalizeQuestionBankText = (document: any, fallback = "") => {
    const text = extractPlainTextFromRichContent(document || null).trim();
    return text || String(fallback || "").trim();
  };

  const base64ToFile = (base64: string, mimeType: string, fileName: string): File => {
    const byteString = window.atob(base64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i += 1) {
      bytes[i] = byteString.charCodeAt(i);
    }
    return new File([bytes], fileName, { type: mimeType || "image/png" });
  };

  const getQuestionImageFile = (question: QuestionBankQuestion): File | null => {
    const richContentImage = (question.questionContent as any)?.blocks?.find((block: any) => block?.type === "image" && block?.image?.base64)?.image;
    const image = question.image?.base64
      ? question.image
      : question.referenceImage?.base64
      ? question.referenceImage
      : richContentImage?.base64
      ? richContentImage
      : null;
    if (!image?.base64) return null;
    const extension = image.mimeType?.includes("jpeg") || image.mimeType?.includes("jpg") ? "jpg" : "png";
    return base64ToFile(image.base64, image.mimeType || "image/png", image.fileName || `dizgici-soru-${question.sira || "resim"}.${extension}`);
  };

  const buildQuestionOptions = (question: QuestionBankQuestion) => {
    const visibleKeys = (["A", "B", "C", "D", "E"] as ChoiceKey[]).slice(0, question.secenek_sayisi || 5);
    const options = visibleKeys.map((key) => normalizeQuestionBankText(question.choiceContents?.[key], question.siklar?.[key] || key) || key);
    return {
      keys: visibleKeys,
      options: options.length >= 2 ? options : ["A", "B", "C", "D"].slice(0, Math.max(2, question.secenek_sayisi || 4)),
    };
  };

  const importQuestionBankQuestions = async (targetContentId: string, questionBankId: string) => {
    const draft = await ApiService.getQuestionBank(questionBankId);
    const sourceQuestions = Array.isArray(draft?.content?.sorular) ? draft.content.sorular : [];

    if (sourceQuestions.length === 0) {
      throw new Error("Seçilen Dizgici kaydında aktarılacak soru yok.");
    }

    for (const question of sourceQuestions) {
      const imageFile = getQuestionImageFile(question);
      const { keys, options } = buildQuestionOptions(question);
      const answerIndex = question.dogru_cevap ? keys.indexOf(question.dogru_cevap) : -1;
      const correctOption = answerIndex >= 0 ? options[answerIndex] || options[0] : options[0];
      const questionTitle = normalizeQuestionBankText(question.questionContent, question.soru_metni || `Soru ${question.sira || ""}`);

      const formData = new FormData();
      formData.append("type", imageFile ? "IMAGE" : "TEXT");
      formData.append("content", questionTitle || `Soru ${question.sira || ""}`.trim());
      formData.append("options", JSON.stringify(options));
      formData.append("correctOption", JSON.stringify(correctOption));
      formData.append(
        "metadata",
        JSON.stringify({
          source: "question_bank_indesign",
          questionBankId: draft.originalQuestionBankId || draft.id,
          questionBankTitle: draft.testAdi,
          questionBankQuestionId: question.id,
          questionNumber: question.sira,
        })
      );
      if (imageFile) {
        formData.append("file", imageFile);
      }

      await ApiService.createLessonSectionContentQuestion(lessonId, sectionId, targetContentId, formData);
    }

    return sourceQuestions.length;
  };

  const createEmptyExamQuestion = (): ExamQuestion => ({
    id: `${Date.now()}-${Math.random()}`,
    type: "TEXT",
    question: "",
    options: ["Seçenek 1", "Seçenek 2", "Seçenek 3", "Seçenek 4"],
    correctAnswer: "",
    metadata: {},
  });

  const getExamQuestions = (): ExamQuestion[] => {
    const raw = (content.metadata as any)?.questions;
    if (Array.isArray(raw)) return raw as ExamQuestion[];
    return [];
  };

  const setExamQuestions = (questions: ExamQuestion[]) => {
    setContent((prev) => ({
      ...prev,
      metadata: {
        ...(prev.metadata || {}),
        questions,
      },
    }));
  };

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    const selectedType = TYPES.find((t) => t.key === content.type);
    if (selectedType?.requiresFile && selectedType.acceptedFiles && selectedType.acceptedFiles !== "*/*") {
      const acceptedTypes = selectedType.acceptedFiles.split(",").map((t) => t.trim());
      const isAccepted = acceptedTypes.some((acceptedType) => {
        if (acceptedType.endsWith("/*")) {
          return file.type.startsWith(acceptedType.replace("/*", "/"));
        }
        if (acceptedType.startsWith(".")) {
          return file.name.toLowerCase().endsWith(acceptedType.toLowerCase());
        }
        return file.type === acceptedType || (acceptedType === "image/jpg" && file.type === "image/jpeg");
      });

      if (!isAccepted) {
        await showAlert({
          title: "Hata",
          message:
            selectedType.key === "IMAGE"
              ? "Bu içerik türü için geçersiz dosya formatı. Sadece JPG ve JPEG formatları desteklenmektedir."
              : `Bu içerik türü için geçersiz dosya formatı. Desteklenen format: ${selectedType.acceptedFiles}`,
          type: "error",
        });
        return;
      }
    }

    const maxSizeMb = 100;
    if (file.size > maxSizeMb * 1024 * 1024) {
      await showAlert({
        title: "Hata",
        message: `Dosya boyutu ${maxSizeMb}MB'den küçük olmalıdır.`,
        type: "error",
      });
      return;
    }

    setUploadedFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleExamQuestionChange = (qIndex: number, field: keyof ExamQuestion, value: any) => {
    const questions = getExamQuestions();
    const updated = [...questions];
    if (updated[qIndex]) {
      updated[qIndex] = { ...updated[qIndex], [field]: value };
      setExamQuestions(updated);
    }
  };

  const handleExamOptionChange = (qIndex: number, optionIndex: number, value: string) => {
    const questions = getExamQuestions();
    const updated = [...questions];
    if (updated[qIndex]) {
      const oldOption = updated[qIndex].options[optionIndex];
      const newOptions = [...updated[qIndex].options];
      newOptions[optionIndex] = value;

      let newCorrectAnswer = updated[qIndex].correctAnswer;
      if (updated[qIndex].correctAnswer === oldOption) {
        newCorrectAnswer = value;
      }

      updated[qIndex] = {
        ...updated[qIndex],
        options: newOptions,
        correctAnswer: newCorrectAnswer,
      };
      setExamQuestions(updated);
    }
  };

  const addExamOption = (qIndex: number) => {
    const questions = getExamQuestions();
    const updated = [...questions];
    if (updated[qIndex]) {
      const newOptionText = `Seçenek ${updated[qIndex].options.length + 1}`;
      updated[qIndex] = {
        ...updated[qIndex],
        options: [...updated[qIndex].options, newOptionText],
      };
      setExamQuestions(updated);
    }
  };

  const removeExamOption = (qIndex: number, optionIndex: number) => {
    const questions = getExamQuestions();
    const updated = [...questions];
    if (updated[qIndex] && updated[qIndex].options.length > 2) {
      const removedOption = updated[qIndex].options[optionIndex];
      const newOptions = updated[qIndex].options.filter((_, idx) => idx !== optionIndex);

      let newCorrectAnswer = updated[qIndex].correctAnswer;
      if (updated[qIndex].correctAnswer === removedOption) {
        newCorrectAnswer = "";
      }

      updated[qIndex] = {
        ...updated[qIndex],
        options: newOptions,
        correctAnswer: newCorrectAnswer,
      };
      setExamQuestions(updated);
    }
  };

  const addExamQuestion = () => {
    const questions = getExamQuestions();
    setExamQuestions([...questions, createEmptyExamQuestion()]);
  };

  const removeExamQuestion = (qIndex: number) => {
    const questions = getExamQuestions();
    if (questions.length > 1) {
      setExamQuestions(questions.filter((_, idx) => idx !== qIndex));
    }
  };

  const validateExam = (): string | null => {
    const questions = getExamQuestions();
    if (questions.length === 0) return "En az bir soru ekleyin.";

    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question.trim()) return `Soru ${i + 1} metnini giriniz.`;
      if (q.options.length < 2) return `Soru ${i + 1} için en az 2 seçenek olmalı.`;
      if (q.options.some((opt) => !opt.trim())) return `Soru ${i + 1} tüm seçenek metinlerini doldurun.`;
      if (!q.correctAnswer || (typeof q.correctAnswer === "string" && !q.correctAnswer.trim())) {
        return `Soru ${i + 1} için doğru cevabı seçin.`;
      }
      const correctAnswerStr = typeof q.correctAnswer === "string" ? q.correctAnswer : q.correctAnswer[0];
      if (!q.options.includes(correctAnswerStr)) {
        return `Soru ${i + 1} için geçersiz doğru cevap seçimi.`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    if (!contentId && !canCreate) {
      await showAlert({
        title: "Yetki Hatası",
        message: "İçerik ekleme yetkiniz yok.",
        type: "error",
      });
      return;
    }
    if (contentId && !canUpdate) {
      await showAlert({
        title: "Yetki Hatası",
        message: "İçerik güncelleme yetkiniz yok.",
        type: "error",
      });
      return;
    }
    if (!content.title.trim()) {
      await showAlert({
        title: "Hata",
        message: "Lütfen içerik başlığını giriniz.",
        type: "error",
      });
      return;
    }

    if (isQuestionBankImport && !selectedQuestionBankId) {
      await showAlert({
        title: "Hata",
        message: "Lütfen aktarılacak Dizgici testini seçiniz.",
        type: "error",
      });
      return;
    }

    const selectedType = TYPES.find((t) => t.key === content.type);
    if (
      selectedType?.requiresFile &&
      !uploadedFile &&
      !contentId &&
      !(content.type === "VIDEO" && existingVideoSourceUrl)
    ) {
      await showAlert({
        title: "Hata",
        message:
          content.type === "VIDEO"
            ? "Lütfen yüklemek için bir video dosyası seçiniz."
            : "Bu içerik türü için bir dosya seçmelisiniz.",
        type: "error",
      });
      return;
    }

    if (content.type === "VIDEO" && videoSourceMode === "vimeo") {
      if (!vimeoUrl.trim()) {
        await showAlert({
          title: "Hata",
          message: "Lütfen Vimeo video linkini giriniz.",
          type: "error",
        });
        return;
      }
      const normalizedVimeo = normalizeVimeoUrl(vimeoUrl);
      if (!validateVimeoUrl(normalizedVimeo)) {
        await showAlert({
          title: "Hata",
          message: "Geçerli bir Vimeo linki giriniz. Örnek: https://vimeo.com/123456789",
          type: "error",
        });
        return;
      }
    }

    if (!content.content.trim()) {
      await showAlert({
        title: "Hata",
        message: "Lütfen içerik açıklamasını giriniz.",
        type: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const nextMetadata: Record<string, any> = { ...(content.metadata || {}) };
      const trimmedDescription = content.content.trim();

      if (effectiveContentType === "VIDEO") {
        if (trimmedDescription) {
          nextMetadata.videoDescription = trimmedDescription;
        } else {
          delete nextMetadata.videoDescription;
        }
      }

      const vimeoContent =
        effectiveContentType === "VIDEO" && videoSourceMode === "vimeo"
          ? normalizeVimeoUrl(vimeoUrl)
          : effectiveContentType === "VIDEO" && !uploadedFile && isLegacyVimeoUrl(existingVideoSourceUrl)
          ? existingVideoSourceUrl
          : null;

      const request: any = {
        title: content.title.trim(),
        type: effectiveContentType,
        content: vimeoContent !== null ? vimeoContent : trimmedDescription,
        metadata: nextMetadata,
      };

      let createdContent: any = null;

      if (contentId) {
        await ApiService.updateContent(lessonId, sectionId, contentId, request, uploadedFile || undefined);
      } else {
        const formData = new FormData();
        if (uploadedFile) {
          formData.append("file", uploadedFile);
        }
        formData.append("title", content.title);
        formData.append("type", effectiveContentType);
        formData.append("content", request.content);
        formData.append("metadata", JSON.stringify(nextMetadata));
        createdContent = await ApiService.createContent(lessonId, sectionId, formData);
      }

      if (!contentId && isQuestionBankImport && selectedQuestionBankId) {
        const targetContentId = createdContent?.id || createdContent?.data?.id;
        if (!targetContentId) {
          throw new Error("Test içeriği oluşturuldu fakat soru aktarımı için içerik kimliği alınamadı.");
        }
        await importQuestionBankQuestions(targetContentId, selectedQuestionBankId);
      }

      await showAlert({
        title: "Başarılı",
        message: contentId
          ? "İçerik başarıyla güncellendi."
          : isQuestionBankImport
          ? "Test Dizgiciden aktarılarak oluşturuldu."
          : "İçerik başarıyla oluşturuldu.",
        type: "success",
      });

      onClose();
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error.message || "İçerik kaydedilirken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={contentId ? "İçeriği Düzenle" : "Yeni İçerik Ekle"} size="lg">
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            İçerik Başlığı <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="İçerik başlığını giriniz"
            value={content.title}
            onChange={(e) => setContent({ ...content, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            İçerik Türü <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
            {TYPES.map((type) => {
              const IconComponent = type.icon;
              return (
                <button
                  key={type.key}
                  type="button"
                  onClick={() => {
                    const nextType = type.key;
                    setContent({
                      ...content,
                      type: nextType,
                      content: content.content,
                    });
                    setUploadedFile(null);
                    setExistingVideoSourceUrl("");
                    setExistingVideoName("");
                    setVideoSourceMode("vimeo");
                    setVimeoUrl("");
                    if (nextType === "QUESTION_BANK_IMPORT") {
                      loadQuestionBanks();
                    } else {
                      setShowQuestionBankImport(false);
                      setSelectedQuestionBankId("");
                    }
                  }}
                  className={`flex flex-col items-center justify-center p-3 border-2 rounded-lg transition-all ${
                    content.type === type.key
                      ? "border-purple-500 bg-purple-50 text-purple-700"
                      : "border-gray-200 hover:border-gray-300 text-gray-600"
                  }`}>
                  <IconComponent className="w-5 h-5 mb-1" />
                  <span className="font-medium text-xs text-center leading-tight">{type.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {isQuestionBankImport && !contentId && (
          <div className="border border-blue-100 bg-blue-50 rounded-lg p-4 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-blue-100">
                <FileSpreadsheet className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">Dizgiciden Test Aktar</div>
                <div className="text-xs text-gray-600 mt-1">
                  Seçilen Dizgici kaydının soru resmi, başlığı, şıkları ve doğru cevabı test sorularına aktarılır.
                </div>
              </div>
            </div>

            {showQuestionBankImport && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dizgici Testi</label>
                <select
                  value={selectedQuestionBankId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    setSelectedQuestionBankId(nextId);
                    const draft = questionBanks.find((item) => getQuestionBankImportId(item) === nextId);
                    if (draft) {
                      setContent((prev) => ({
                        ...prev,
                        title: prev.title.trim() ? prev.title : draft.testAdi,
                        content: draft.testAdi,
                        metadata: {
                          ...(prev.metadata || {}),
                          source: "question_bank_indesign",
                          questionBankId: getQuestionBankImportId(draft),
                          questionBankTitle: draft.testAdi,
                        },
                      }));
                    }
                  }}
                  disabled={questionBanksLoading}
                  className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                  <option value="">{questionBanksLoading ? "Dizgici kayıtları yükleniyor..." : "Dizgici testi seçin"}</option>
                  {questionBanks.map((draft) => (
                    <option key={draft.id} value={getQuestionBankImportId(draft)}>
                      {draft.testAdi} ({draft.questionCount || 0} soru)
                    </option>
                  ))}
                </select>
                {showQuestionBankImport && !questionBanksLoading && questionBanks.length === 0 && (
                  <div className="mt-2 text-xs text-gray-600">Aktarılabilecek Dizgici kaydı bulunamadı.</div>
                )}
                {selectedQuestionBank && (
                  <div className="mt-2 text-xs text-blue-800">
                    Seçili kayıt: {selectedQuestionBank.testAdi} - {selectedQuestionBank.questionCount || 0} soru.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* İçerik Açıklama Alanı */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            İçerik Açıklaması <span className="text-red-500">*</span>
          </label>
          <textarea
            placeholder={
              content.type === "CODE"
                ? "Kodunuzu buraya yazın..."
                : effectiveContentType === "EXAM"
                ? "Test açıklaması yazın..."
                : effectiveContentType === "VIDEO"
                ? "Video açıklaması yazın..."
                : effectiveContentType === "AUDIO"
                ? "Ses açıklaması yazın..."
                : effectiveContentType === "IMAGE"
                ? "Resim açıklaması yazın..."
                : effectiveContentType === "FILE"
                ? "Dosya açıklaması yazın..."
                : "İçerik açıklaması yazın..."
            }
            value={content.content}
            onChange={(e) => setContent({ ...content, content: e.target.value })}
            rows={content.type === "CODE" ? 8 : 4}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none ${
              content.type === "CODE" ? "font-mono text-sm" : ""
            }`}
          />
        </div>

        {/* Video Kaynak Seçimi */}
        {content.type === "VIDEO" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Video Kaynağı <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => { setVideoSourceMode("vimeo"); setUploadedFile(null); }}
                className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  videoSourceMode === "vimeo"
                    ? "border-purple-500 bg-purple-50 text-purple-700"
                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                }`}>
                Vimeo URL
              </button>
              <button
                type="button"
                onClick={() => { setVideoSourceMode("file"); setVimeoUrl(""); }}
                className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  videoSourceMode === "file"
                    ? "border-purple-500 bg-purple-50 text-purple-700"
                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                }`}>
                Dosya Yükle
              </button>
            </div>
            {videoSourceMode === "vimeo" && (
              <div>
                <input
                  type="url"
                  placeholder="https://vimeo.com/123456789 veya https://player.vimeo.com/video/123456789"
                  value={vimeoUrl}
                  onChange={(e) => setVimeoUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div className="mt-2 text-xs text-gray-500">
                  Desteklenen formatlar: vimeo.com/videoID veya player.vimeo.com/video/videoID
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dosya Yükleme Alanı */}
        {TYPES.find((t) => t.key === content.type)?.requiresFile && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {content.type === "VIDEO" ? "Video Dosyası" : "Dosya"} <span className="text-red-500">*</span>
            </label>
            {content.type === "VIDEO" && existingVideoSourceUrl && !uploadedFile ? (
              <div className="mb-3 border border-blue-200 rounded-lg p-4 bg-blue-50">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-blue-900">
                      Mevcut video hazır
                    </div>
                    <div className="text-xs text-blue-700 mt-1 break-all">
                      {existingVideoName || existingVideoSourceUrl}
                    </div>
                    <div className="text-xs text-blue-700 mt-2">
                      Yeni dosya seçersen mevcut video CDN kaydı ile değiştirilir.
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            {uploadedFile ? (
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-green-600 mr-2">✓</span>
                    <span className="text-sm font-medium">{uploadedFile.name}</span>
                    <span className="text-xs text-gray-500 ml-2">({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                  </div>
                  <button type="button" onClick={() => setUploadedFile(null)} className="text-red-600 hover:text-red-800 text-sm">
                    Kaldır
                  </button>
                </div>
              </div>
            ) : (
              <input
                type="file"
                accept={TYPES.find((t) => t.key === content.type)?.acceptedFiles}
                onChange={handleFileInputChange}
                className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
              />
            )}
            {content.type === "VIDEO" ? (
              <div className="mt-2 text-xs text-gray-500">
                Desteklenen formatlar: MP4, MOV, WebM, MKV, AVI. Yüklenen dosya CDN’e kaydedilir.
              </div>
            ) : content.type === "AUDIO" ? (
              <div className="mt-2 text-xs text-gray-500">
                Desteklenen formatlar: M4A, MP3, WAV, OGG.
              </div>
            ) : null}
          </div>
        )}

        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50">
            İptal
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || (!contentId && !canCreate) || (!!contentId && !canUpdate)}
            className={`px-6 py-2 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 ${
              contentId ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"
            }`}>
            {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
            {loading ? "Kaydediliyor..." : contentId ? "Güncelle" : "Oluştur"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
